import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, TextField, Button, CircularProgress,
    InputAdornment, ToggleButtonGroup, ToggleButton, Collapse, Typography, Checkbox, FormControlLabel,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon, List as ListIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO05 from '../../../store/actions/indexNO05';
import NO05, { NO05SearchParams } from '../../../models/NO05';
import { filterByDate } from '../no05Stats';
import { DeliveryFilter, filterByStatus, getVisibleOrders, isDelvisLevert, isKomplettLevert } from '../no05VisibleOrders';
import * as APICalls from '../../../felles/APICalls_NO05';
import CSharpUtils from '../../../felles/CSharpUtils';
import Utils from '../../../felles/Utils';

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        padding: theme.spacing(1, 1.5),
        margin: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0,
    },
    quickSearchWrapper: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    quickSearchField: {
        width: '160px',
        flexShrink: 0,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5', height: '34px', fontSize: '0.85rem' },
        '& .MuiInputBase-input': { padding: '6px 10px', fontSize: '0.85rem' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
    },
    searchModeToggle: {
        '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.72rem', padding: theme.spacing(0.25, 1) },
        '& .MuiToggleButton-root.Mui-selected': {
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            '&:hover': { backgroundColor: '#d7edd9' },
        },
    },
}));

const CHECKBOX_SX = { p: 0.5, color: '#d30535', '&.Mui-checked': { color: '#d30535' } };

export default function NO05Search() {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const companyId = useSelector((state: AppState) => state.auth.userJWT?.Department ?? '');
    const orders = useSelector((state: AppState) => state.NO05AapneOrdre.orders);
    const selectedStatus = useSelector((state: AppState) => state.NO05AapneOrdre.selectedStatus);
    const selectedOrderNrs = useSelector((state: AppState) => state.NO05AapneOrdre.selectedOrderNrs);
    const selectedDate = useSelector((state: AppState) => state.NO05AapneOrdre.selectedDate);
    const deliveryFilter = useSelector((state: AppState) => state.NO05AapneOrdre.deliveryFilter);

    // Status card + Ordredato box, before the leveringsfilter — the base the filter counts are
    // taken from, so "Komplett levert (n)" says how many the filter would leave.
    const baseOrders = useMemo(
        () => filterByStatus(filterByDate(orders, selectedDate), selectedStatus),
        [orders, selectedStatus, selectedDate],
    );
    const komplettCount = useMemo(() => baseOrders.filter(isKomplettLevert).length, [baseOrders]);
    const delvisCount = useMemo(() => baseOrders.filter(isDelvisLevert).length, [baseOrders]);

    // Exactly what the table shows — "Velg alle" and the Excel exports work on this.
    const displayOrders = useMemo(
        () => getVisibleOrders(orders, selectedStatus, selectedDate, deliveryFilter),
        [orders, selectedStatus, selectedDate, deliveryFilter],
    );

    // Only one filter at a time: ticking one replaces the other, unticking the active one clears it.
    const toggleDeliveryFilter = (filter: DeliveryFilter) =>
        dispatch(actionsNO05.NO05SetDeliveryFilter(deliveryFilter === filter ? '' : filter));

    const selected = useMemo(() => new Set(selectedOrderNrs), [selectedOrderNrs]);
    const allSelected = displayOrders.length > 0 && displayOrders.every((o: NO05) => selected.has(o.ordreNr));

    // Each search mode remembers its own text — switching modes shows that mode's own value
    // (or blank if nothing was typed for it yet), and switching back restores it, rather than
    // sharing one field's text across both modes.
    const [quickSearchValues, setQuickSearchValues] = useState<{ order: string; customer: string }>({ order: '', customer: '' });
    const [searchMode, setSearchMode] = useState<'order' | 'customer'>('order');
    const quickSearch = quickSearchValues[searchMode];
    const setQuickSearch = (value: string) => setQuickSearchValues((prev) => ({ ...prev, [searchMode]: value }));
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advancedState, setAdvancedState] = useState({ requisitionNumber: '', productId: '', receiver: '', location: '', address: '', department: '', warehouse: '' });
    const [exportingLines, setExportingLines] = useState(false);

    const buildParams = (): NO05SearchParams => {
        const base: NO05SearchParams = { companyId };
        const v = quickSearch.trim();
        if (!v) return base;
        if (searchMode === 'order') base.orderNumber = v;
        else base.customerNumber = v;
        return base;
    };

    const handleSearch = () => {
        if (!quickSearch.trim()) return;
        dispatch(actionsNO05.NO05SearchRequest(buildParams()));
    };

    const buildAdvancedParams = (): NO05SearchParams => {
        const base: NO05SearchParams = { companyId };
        if (advancedState.requisitionNumber.trim()) base.requisitionNumber = advancedState.requisitionNumber.trim();
        if (advancedState.productId.trim())         base.productId         = advancedState.productId.trim();
        if (advancedState.receiver.trim())          base.receiver          = advancedState.receiver.trim();
        if (advancedState.location.trim())      base.location      = advancedState.location.trim();
        if (advancedState.address.trim())           base.address           = advancedState.address.trim();
        if (advancedState.department.trim())        base.department        = advancedState.department.trim();
        if (advancedState.warehouse.trim())         base.warehouse         = advancedState.warehouse.trim();
        return base;
    };

    const handleAdvancedSearch = () => {
        const params = buildAdvancedParams();
        if (!params.requisitionNumber && !params.productId && !params.receiver && !params.location && !params.address && !params.department && !params.warehouse) return;
        dispatch(actionsNO05.NO05SearchRequest(params));
    };

    const resetToAllOpenOrders = () => {
        dispatch(actionsNO05.NO05SetStatus('Alle ordre'));
        dispatch(actionsNO05.NO05SearchRequest({ companyId, skipPaging: true }));
    };

    const handleAdvancedClear = () => {
        setAdvancedState({ requisitionNumber: '', productId: '', receiver: '', location: '', address: '', department: '', warehouse: '' });
        resetToAllOpenOrders();
    };

    const triggerDownload = (content: string, filename: string) => {
        const el = document.createElement('a');
        el.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent('﻿' + content)}`);
        el.setAttribute('download', filename);
        document.body.appendChild(el);
        el.click();
        document.body.removeChild(el);
    };

    const exportOrdersToCSV = () => {
        const toExport = selected.size > 0 ? displayOrders.filter((o) => selected.has(o.ordreNr)) : displayOrders;
        if (!toExport.length) return;
        const headers = ['OrdreNr','Dato','Tid','Kundenr','Navn','RekvNr','OM','Status','Bestilt','Levert','Beløp','BRF%','Faktura','Sporing','SendeMåte','Speditor','BK','BM'];
        const rows = toExport.map((o) => [
            Utils.csvText(o.ordreNr),
            Utils.csvText(o.dato ? new Date(o.dato).toLocaleDateString('nb-NO') : ''),
            Utils.csvText(o.tid ? `${o.tid.toString().padStart(6,'0').substring(0,2)}:${o.tid.toString().padStart(6,'0').substring(2,4)}` : ''),
            Utils.csvText(o.kundenr), Utils.csvText(o.navn), Utils.csvText(o.rekvNr), Utils.csvText(o.om), Utils.csvText(o.status),
            // Numeric — left unwrapped (not csvText) so Excel can SUM these columns.
            Utils.csvNumber(o.antBestilt), Utils.csvNumber(o.antLevert), Utils.csvNumber(o.belop), Utils.csvNumber(o.brfProsent),
            Utils.csvText(o.faktura), Utils.csvText(o.fraktEdi), Utils.csvText(o.sendeMote), Utils.csvText(o.speditor),
            Utils.csvText(o.bk), Utils.csvText(o.betalingsKode),
        ].join(';'));
        triggerDownload([headers.join(';'), ...rows].join('\n'), `AapneOrdre_${Date.now()}.csv`);
    };

    // Med ordrelinjer — one batched request for every selected order's lines, then exports
    // flattened rows. Used to call fetchOrderDetail once per order (up to 24 at a time from the
    // browser); GetOrderLinesForExportHandler does the equivalent lookup server-side in a single
    // batched query, which is both fewer DB round trips and just one HTTP round trip overall.
    const exportOrdersWithLines = async () => {
        const toExport = selected.size > 0 ? displayOrders.filter((o) => selected.has(o.ordreNr)) : displayOrders;
        if (!toExport.length) return;
        setExportingLines(true);
        try {
            const companyId = Number(CSharpUtils.T_companyID);
            const headers = ['OrdreNr','Dato','Kundenr','Navn','RekvNr','Status','BK','BM','Linje','Alfa','Artikkel','Bestilt','Levert','Tekst','VPris','NPris','Tillegg'];

            const res = await APICalls.fetchOrderLinesForExport(companyId, toExport.map((o) => ({ orderNumber: o.ordreNr, recordType: o.recordType })));
            const ordersByNumber = new Map<string, any>(
                (res.data?.orders ?? []).map((o: any) => [`${o.recordType}|${o.orderNumber}`, o])
            );

            const rows = toExport.flatMap((order): string[] => {
                const detail = ordersByNumber.get(`${order.recordType}|${order.ordreNr}`);
                const dato = order.dato ? new Date(order.dato).toLocaleDateString('nb-NO') : '';
                const bk = detail?.bk ?? '';
                const bm = detail?.paymentMethod ?? '';
                const lines: any[] = detail?.lines ?? [];
                const orderCols = [order.ordreNr, dato, order.kundenr, order.navn, order.rekvNr, order.status, bk, bm].map(Utils.csvText);
                if (!lines.length) {
                    return [[...orderCols, '', '', '', '', '', '', '', '', ''].join(';')];
                }
                // Retur orders (order number starting with '9') come back with positive line
                // amounts even though they're a credit — same case NO05.saga.ts handles for
                // `belop` on the order list. Flip the sign here too so a "med ordrelinjer" export
                // reads consistently with the plain "Excel" export.
                const isRetur = order.ordreNr.toString().startsWith('9');
                const signed = (n: number | undefined) => (isRetur && typeof n === 'number' ? -Math.abs(n) : n);
                // Bestilt/Levert/VPris/NPris/Tillegg left unwrapped (not csvText)
                // so Excel can SUM these columns.
                return lines.map((l: any) => [
                    ...orderCols,
                    Utils.csvText(l.position), Utils.csvText(l.alpha), Utils.csvText(l.articleNumber),
                    Utils.csvNumber(l.quantityOrdered), Utils.csvNumber(l.quantityDelivered),
                    Utils.csvText(l.itemText),
                    Utils.csvNumber(signed(l.price)), Utils.csvNumber(signed(l.netPrice)), Utils.csvNumber(signed(l.surcharge)),
                ].join(';'));
            });
            triggerDownload([headers.join(';'), ...rows].join('\n'), `AapneOrdre_MedLinjer_${Date.now()}.csv`);
        } finally {
            setExportingLines(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') handleSearch();
        else if (event.key === 'Escape') {
            setQuickSearchValues({ order: '', customer: '' });
            resetToAllOpenOrders();
        }
    };

    return (
        <Paper className={classes.searchContainer} elevation={0}>

            {/* Quick search — hidden when advanced is open — toggle + field + buttons in one row */}
            {!advancedOpen && (
                <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <ToggleButtonGroup
                        value={searchMode}
                        exclusive
                        size="small"
                        onChange={(_, val) => { if (val) setSearchMode(val); }}
                        className={classes.searchModeToggle}
                    >
                        <ToggleButton value="order">Ordrenummer</ToggleButton>
                        <ToggleButton value="customer">Kundenummer</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                        size="small"
                        placeholder={searchMode === 'order' ? 'Ordrenr' : 'Kundenr'}
                        value={quickSearch}
                        onChange={(e) => setQuickSearch(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={classes.quickSearchField}
                        inputProps={{ maxLength: 6 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleSearch}
                        sx={{ height: '34px', textTransform: 'none', fontSize: '0.82rem', px: 1.25, minWidth: '72px', backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                    >
                        Søk
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => { setQuickSearchValues({ order: '', customer: '' }); resetToAllOpenOrders(); }}
                        sx={{ height: '34px', textTransform: 'none', fontSize: '0.82rem', px: 1.25, minWidth: '72px', borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c' } }}
                    >
                        Nullstill
                    </Button>
                </Box>
            )}

            {/* Advanced search toggle */}
            <Box
                onClick={() => setAdvancedOpen(!advancedOpen)}
                sx={{ display: 'flex', alignItems: 'center', mt: advancedOpen ? 0 : 0.75, cursor: 'pointer', width: 'fit-content', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
            >
                {advancedOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
                </Typography>
            </Box>

            {/* Advanced section */}
            <Collapse in={advancedOpen}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.75, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        label="Rekv.nr"
                        placeholder="F.eks. ED23433"
                        value={advancedState.requisitionNumber}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, requisitionNumber: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 150 }}
                    />
                    <TextField
                        size="small"
                        label="Alfa/Artikkel"
                        placeholder="F.eks. 1234567"
                        value={advancedState.productId}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, productId: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 220 }}
                    />
                    <TextField
                        size="small"
                        label="OM"
                        placeholder="F.eks. KJ"
                        value={advancedState.receiver}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, receiver: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 80 }}
                        inputProps={{ maxLength: 2 }}
                    />
                    <TextField
                        size="small"
                        label="Lokasjon"
                        placeholder="F.eks. C3-03"
                        value={advancedState.location}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, location: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 150 }}
                    />
                    <TextField
                        size="small"
                        label="Vareadresse"
                        placeholder="F.eks. Frank"
                        value={advancedState.address}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, address: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 150 }}
                    />
                    <TextField
                        size="small"
                        label="Avdeling"
                        placeholder="F.eks. 1"
                        value={advancedState.department}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, department: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 100 }}
                    />
                    <TextField
                        size="small"
                        label="Lagerkode"
                        placeholder="F.eks. 10"
                        value={advancedState.warehouse}
                        onChange={(e) => setAdvancedState(prev => ({ ...prev, warehouse: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdvancedSearch(); }}
                        className={classes.quickSearchField}
                        style={{ width: 130 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleAdvancedSearch}
                        sx={{ height: '34px', textTransform: 'none', fontSize: '0.82rem', px: 1.25, minWidth: '72px', backgroundColor: '#d30535', '&:hover': { backgroundColor: '#b0022c' } }}
                    >
                        Søk
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleAdvancedClear}
                        sx={{ height: '34px', textTransform: 'none', fontSize: '0.82rem', px: 1.25, minWidth: '72px', borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c' } }}
                    >
                        Nullstill
                    </Button>
                </Box>
            </Collapse>

            {/* Export / select row — always visible once orders are loaded */}
            {orders.length > 0 && (
                <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.75}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={deliveryFilter === 'komplett'} onChange={() => toggleDeliveryFilter('komplett')} sx={CHECKBOX_SX} />}
                            label={'Komplett levert (' + komplettCount + ')'}
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.82rem', whiteSpace: 'nowrap' } }}
                        />
                        <FormControlLabel
                            control={<Checkbox size="small" checked={deliveryFilter === 'delvis'} onChange={() => toggleDeliveryFilter('delvis')} sx={CHECKBOX_SX} />}
                            label={'Delvis levert (' + delvisCount + ')'}
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.82rem', whiteSpace: 'nowrap' } }}
                        />
                        <Button size="small" variant="outlined"
                            onClick={() => dispatch(actionsNO05.NO05SetSelected(allSelected ? [] : displayOrders.map((o: NO05) => o.ordreNr)))}
                            sx={{ height: '36px', textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#d30535', color: '#d30535', '&:hover': { borderColor: '#b0022c', color: '#b0022c', backgroundColor: 'rgba(211,5,53,0.04)' } }}>
                            Velg alle
                        </Button>
                    </Box>
                    <Box display="flex" gap={1}>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportOrdersToCSV}
                            sx={{ height: '36px', textTransform: 'none', whiteSpace: 'nowrap', color: '#555', borderColor: '#aaa', '&:hover': { borderColor: '#555', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            Excel
                        </Button>
                        <Button size="small" variant="outlined" startIcon={exportingLines ? <CircularProgress size={14} /> : <ListIcon />}
                            onClick={exportOrdersWithLines} disabled={exportingLines}
                            sx={{ height: '36px', textTransform: 'none', whiteSpace: 'nowrap', color: '#555', borderColor: '#aaa', '&:hover': { borderColor: '#555', backgroundColor: 'rgba(0,0,0,0.04)' } }}>
                            Med ordrelinjer
                        </Button>
                    </Box>
                </Box>
            )}
        </Paper>
    );
}
